import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { PicnicService } from 'src/app/services/api/picnic.service';
import {
  CuadroPicnicItem,
  GroupedPicnicServiceItem,
  PassengerDietaryRestrictionDetail,
  PicnicActivityDetailsData,
} from '../models/cuadro-picnic.model';
import {
  filterValidDietaryRestrictions,
  getDietaryBadgeClass,
  getDietaryBadgeIcon,
  parsePassengerDietaryRestrictions,
} from '../utils';

/**
 * Interface defining the payload passed into DialogPicnicDetailsComponent.
 */
export interface PicnicDetailsModalData {
  picnicItemRow?: CuadroPicnicItem;
  idActivity?: string;
  idItinerary?: string;
  foodType?: string;
  paxCount?: number;
  tcCount?: number;
}

/**
 * Backward compatibility alias for legacy imports.
 */
export type DetallesPicnicModalData = PicnicDetailsModalData;

/**
 * Dialog component displaying detailed operational breakdown for a Picnic service row.
 * Loads comprehensive activity details on-demand via getPicnicActivityDetails GraphQL endpoint,
 * including Pax/TC profiles, dietary restrictions, and grouped AYB services with counts.
 */
@Component({
  selector: 'app-dialog-picnic-details',
  templateUrl: './dialog-picnic-details.component.html',
  styleUrls: ['./dialog-picnic-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogPicnicDetailsComponent implements OnInit, OnDestroy {
  /**
   * Indicates whether on-demand activity details are currently being fetched from API.
   */
  public isLoadingDetails = false;

  /**
   * Dedicated activity details payload returned by getPicnicActivityDetails.
   */
  public activityDetails: PicnicActivityDetailsData | null = null;

  /**
   * Unsubscribe subject triggered on component destruction.
   */
  private readonly _unsubscribeOnDestroy$ = new Subject<void>();

  /**
   * Initializes the modal with injected dialog data, dialog reference, change detector, and picnic API service.
   *
   * Description:
   *   Sets up component dependencies for dialog management and data fetching.
   *
   * Parameters:
   *   dialogReference: MatDialogRef<DialogPicnicDetailsComponent> - Dialog controller reference.
   *   modalInjectData: DetallesPicnicModalData - Injected row data and activity identifiers.
   *   changeDetectorRef: ChangeDetectorRef - Change detection trigger for OnPush strategy.
   *   picnicService: PicnicService - GraphQL service client.
   *
   * Return Value:
   *   DialogPicnicDetailsComponent instance.
   *
   * Exceptions & Errors:
   *   None.
   */
  constructor(
    public readonly dialogReference: MatDialogRef<DialogPicnicDetailsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public readonly modalInjectData: DetallesPicnicModalData,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly picnicService: PicnicService,
  ) {}

  /**
   * Lifecycle hook executed after component initialization.
   *
   * Description:
   *   Initiates on-demand query for activity details if activity ID and itinerary ID are provided.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Handles query failures gracefully with fallback to injected row data.
   */
  public ngOnInit(): void {
    this.loadActivityDetails();
  }

  /**
   * Lifecycle hook executed before component destruction.
   *
   * Description:
   *   Emits and completes the unsubscribe subject to clean up active subscriptions.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next();
    this._unsubscribeOnDestroy$.complete();
  }

  /**
   * Fetches dedicated activity details from GraphQL API with recursive cursor pagination.
   *
   * Description:
   *   Calls queryGetPicnicActivityDetails with target activity ID, food type, and pagination cursor.
   *   Continuously sends exclusiveStartKeyId to the backend until lastEvaluatedKeyId is undefined,
   *   accumulating and grouping all Food & Beverage services for this modal.
   *
   * Parameters:
   *   cursorStartKey: string | undefined - Optional pagination cursor for subsequent pages.
   *
   * Return Value:
   *   void - Updates component activityDetails state and triggers change detection.
   *
   * Exceptions & Errors:
   *   Logs warning and falls back to local row data on network or query error.
   */
  private loadActivityDetails(cursorStartKey?: string): void {
    const targetActivityId =
      this.modalInjectData?.idActivity ||
      this.modalInjectData?.picnicItemRow?.idActivity;
    const targetFoodType =
      this.modalInjectData?.foodType ||
      this.modalInjectData?.picnicItemRow?.foodType;

    if (!targetActivityId) {
      return;
    }

    if (!cursorStartKey) {
      this.isLoadingDetails = true;
      this.changeDetectorRef.markForCheck();
    }

    this.picnicService
      .queryGetPicnicActivityDetails({
        idActivity: targetActivityId,
        foodType: targetFoodType,
        exclusiveStartKeyId: cursorStartKey,
      })
      .pipe(
        catchError((apiError: any) => {
          console.warn(
            '[DialogPicnicDetailsComponent] Could not fetch detailed activity details from API, using fallback:',
            apiError,
          );
          return of(null);
        }),
        takeUntil(this._unsubscribeOnDestroy$),
      )
      .subscribe({
        next: (graphQLResponse: any) => {
          const detailsPayload = graphQLResponse?.getPicnicActivityDetails;
          if (detailsPayload) {
            if (!this.activityDetails || !cursorStartKey) {
              this.activityDetails = {
                idActivity: detailsPayload.idActivity,
                aybServices: [...(detailsPayload.aybServices || [])],
                lastEvaluatedKeyId: detailsPayload.lastEvaluatedKeyId,
              };
            } else {
              // Accumulate and merge incoming grouped services
              const existingServices = [
                ...(this.activityDetails.aybServices || []),
              ];
              for (const incomingService of detailsPayload.aybServices || []) {
                const existingIndex = existingServices.findIndex(
                  (serviceRecord) =>
                    serviceRecord.destinatarioServicio ===
                      incomingService.destinatarioServicio &&
                    serviceRecord.nombreServicio ===
                      incomingService.nombreServicio &&
                    (serviceRecord.nombreProveedor || '') ===
                      (incomingService.nombreProveedor || ''),
                );
                if (existingIndex >= 0) {
                  existingServices[existingIndex].count +=
                    incomingService.count;
                } else {
                  existingServices.push(incomingService);
                }
              }

              this.activityDetails = {
                idActivity: detailsPayload.idActivity,
                aybServices: existingServices,
                lastEvaluatedKeyId: detailsPayload.lastEvaluatedKeyId,
              };
            }

            // If backend returned another page cursor, continue fetching until undefined
            if (detailsPayload.lastEvaluatedKeyId) {
              this.loadActivityDetails(detailsPayload.lastEvaluatedKeyId);
              return;
            }
          }

          this.isLoadingDetails = false;
          this.changeDetectorRef.markForCheck();
        },
        error: (queryError: any) => {
          console.error(
            '[DialogPicnicDetailsComponent] Error loading activity details:',
            queryError,
          );
          this.isLoadingDetails = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  /**
   * Resolves sales file number.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Sales file number.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get fileNumber(): string {
    return this.modalInjectData?.picnicItemRow?.fileNumber || '';
  }

  /**
   * Resolves whether the current file is a File Padre (Salida Fija).
   *
   * Description:
   *   Checks the isSSFFPadre boolean flag on the injected picnic row data.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if file is a Salida Fija / File Padre, false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get isSSFFPadre(): boolean {
    return Boolean(this.modalInjectData?.picnicItemRow?.isSSFFPadre);
  }

  /**
   * Resolves client / group name.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Client name.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get clientName(): string {
    return this.modalInjectData?.picnicItemRow?.nameClient || '-';
  }

  /**
   * Resolves itinerary / agency name.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Itinerary name.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get itineraryName(): string {
    return this.modalInjectData?.picnicItemRow?.nombreItinerario || '-';
  }

  /**
   * Resolves activity name.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Activity name.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get activityName(): string {
    return (
      this.modalInjectData?.picnicItemRow?.activityName ||
      this.modalInjectData?.picnicItemRow?.servicioNombre ||
      '-'
    );
  }

  /**
   * Backward-compatible alias for activityName.
   */
  public get serviceName(): string {
    return this.activityName;
  }

  /**
   * Resolves active passenger count.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of active PAX.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get activePaxCount(): number {
    if (typeof this.modalInjectData?.paxCount === 'number') {
      return this.modalInjectData.paxCount;
    }
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.activePaxCount ||
      0
    );
  }

  /**
   * Resolves active tour conductor count.
   * Prioritizes explicit tcCount injected from Cuadro Picnic row.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of active TC.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get activeTcCount(): number {
    if (typeof this.modalInjectData?.tcCount === 'number') {
      return this.modalInjectData.tcCount;
    }
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.activeTcCount ||
      0
    );
  }

  /**
   * Resolves the quantity to display in the "Cant." column for a given service item.
   * When destinatarioServicio is "PAX", loads the available PAX count from the row.
   * When destinatarioServicio is "TC", loads the available TC count from the row.
   * Otherwise, loads the aggregated count of that service record.
   *
   * @param {GroupedPicnicServiceItem} serviceItem - Service item row in details table.
   * @returns {number} The resolved quantity.
   */
  public getServiceQuantity(serviceItem: GroupedPicnicServiceItem): number {
    const rawDestinatario = String(serviceItem?.destinatarioServicio || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();

    if (
      rawDestinatario === 'PAX' ||
      rawDestinatario.includes('PAX') ||
      rawDestinatario.includes('PASAJERO')
    ) {
      return this.activePaxCount;
    }

    if (
      rawDestinatario === 'TC' ||
      rawDestinatario.includes('TC') ||
      rawDestinatario.includes('TOUR CONDUCTOR')
    ) {
      return this.activeTcCount;
    }

    return serviceItem?.count || 1;
  }

  /**
   * Resolves staff count.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of staff.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get staffCount(): number {
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.staffCount || 0
    );
  }

  /**
   * Resolves infant passenger count (< 3 years).
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of infant passengers.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get infantCount(): number {
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.infantCount || 0
    );
  }

  /**
   * Resolves child passenger count (3–12 years).
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of child passengers.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get childCount(): number {
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.childCount || 0
    );
  }

  /**
   * Resolves total headcount count for the picnic table.
   *
   * @returns Total diners count combining PAX, TC, staff, infants, and children.
   */
  public get totalMesaCount(): number {
    return (
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails?.totalMesaCount ||
      this.activePaxCount +
        this.activeTcCount +
        this.staffCount +
        this.infantCount +
        this.childCount
    );
  }

  /**
   * Evaluates if any passenger in this group has critical dietary conditions (allergies or celiac/gluten-free).
   *
   * @returns True if critical alert exists; false otherwise.
   */
  public get hasCriticalRestrictions(): boolean {
    return this.getParsedPassengerRestrictions().some(
      (detail) => detail.hasAllergiesOrCritical,
    );
  }

  /**
   * Determines if a specific dietary restriction tag represents a critical medical condition (allergy or celiac).
   *
   * @param tag - Restriction tag label.
   * @returns True if critical; false otherwise.
   */
  public isCriticalRestrictionTag(tag: string): boolean {
    if (!tag) {
      return false;
    }
    const lower = tag.toLowerCase();
    return (
      lower.includes('alerg') ||
      lower.includes('celiac') ||
      lower.includes('gluten')
    );
  }

  /**
   * Resolves dietary restrictions summary list.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string[] - List of formatted dietary restriction strings.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get dietaryRestrictions(): string[] {
    const rawList =
      this.modalInjectData?.picnicItemRow?.picnicGroupDetails
        ?.dietaryRestrictionsList || [];
    return filterValidDietaryRestrictions(rawList);
  }

  /**
   * Parses raw dietary restrictions into structured passenger records and sorts them by restriction severity.
   * Delegated to shared dietary-restrictions utility.
   *
   * @returns Array of structured passenger restriction records sorted by severity descending.
   */
  public getParsedPassengerRestrictions(): PassengerDietaryRestrictionDetail[] {
    return parsePassengerDietaryRestrictions(this.dietaryRestrictions);
  }

  /**
   * Resolves CSS modifier class for a specific restriction badge in the details modal.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param restrictionTag - Formatted restriction label.
   * @returns CSS modifier class name.
   */
  public getRestrictionBadgeClass(restrictionTag: string): string {
    return getDietaryBadgeClass(restrictionTag);
  }

  /**
   * Resolves Tabler icon name appropriate for a specific restriction badge in the details modal.
   * Delegated to shared dietary-restrictions utility.
   *
   * @param restrictionTag - Formatted restriction label.
   * @returns Tabler icon identifier string.
   */
  public getRestrictionBadgeIcon(restrictionTag: string): string {
    return getDietaryBadgeIcon(restrictionTag);
  }

  /**
   * Retrieves grouped AYB food & beverage services sorted alphabetically by Destinatario (A-Z).
   *
   * Description:
   *   Returns services from API response sorted by destinatarioServicio ascending (A-Z) and secondarily by service name.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   GroupedPicnicServiceItem[] - Sorted list of aggregated service items with counts.
   *
   * Exceptions & Errors:
   *   Returns empty array if no services are found.
   */
  public get groupedAybServices(): GroupedPicnicServiceItem[] {
    const rawServices = this.activityDetails?.aybServices || [];
    return [...rawServices].sort((serviceA, serviceB) => {
      const destinatarioA = String(serviceA.destinatarioServicio || '')
        .trim()
        .toUpperCase();
      const destinatarioB = String(serviceB.destinatarioServicio || '')
        .trim()
        .toUpperCase();
      const destinatarioComparison = destinatarioA.localeCompare(destinatarioB);
      if (destinatarioComparison !== 0) {
        return destinatarioComparison;
      }
      const serviceNameA = String(serviceA.nombreServicio || '')
        .trim()
        .toUpperCase();
      const serviceNameB = String(serviceB.nombreServicio || '')
        .trim()
        .toUpperCase();
      return serviceNameA.localeCompare(serviceNameB);
    });
  }

  /**
   * Resolves row CSS class based on Destinatario type for contextual row highlighting.
   * Matches the canonical recipient tones from Cuadro Picnic (Pax -> Green, TC -> Blue, Guia -> Green, Chofer -> Sky, Asist -> Indigo, Staff -> Orange).
   *
   * @param recipient - Beneficiary role string (PAX, TC, GUIA, CHOFER, ASIST GUIA, STAFF, etc.)
   * @returns CSS modifier class name for the table row.
   */
  public resolveRecipientRowClass(recipient?: string): string {
    if (!recipient) return 'row-dest-neutral';
    const code = recipient
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();

    if (code === 'PAX' || code.includes('PAX') || code.includes('PASAJERO')) {
      return 'row-dest-pax';
    }
    if (
      code === 'TC' ||
      code.includes('TC') ||
      code.includes('TOUR CONDUCTOR')
    ) {
      return 'row-dest-tc';
    }
    if (code.includes('GUIA') || code.includes('GUIDE')) {
      if (code.includes('ASIST') || code.includes('ASSIST')) {
        return 'row-dest-asist';
      }
      return 'row-dest-guia';
    }
    if (
      code.includes('CHOFER') ||
      code.includes('DRIVER') ||
      code.includes('CONDUCTOR')
    ) {
      return 'row-dest-chofer';
    }
    if (code.includes('ASIST') || code.includes('ASSIST')) {
      return 'row-dest-asist';
    }
    if (
      code.includes('CAMPO') ||
      code.includes('COCINERO') ||
      code.includes('COOK') ||
      code.includes('STAFF') ||
      code.includes('PERSONAL')
    ) {
      return 'row-dest-staff';
    }
    return 'row-dest-neutral';
  }

  /**
   * Resolves badge color tone class matching the exact destinatarioServicio column tones from the main Cuadro Picnic table.
   *
   * @param recipient - Raw beneficiary identifier string.
   * @returns CSS modifier class name.
   */
  public resolveRecipientBadgeClass(recipient?: string): string {
    const rowClass = this.resolveRecipientRowClass(recipient);
    return rowClass.replace('row-dest-', 'badge-dest-');
  }

  /**
   * Backward-compatible alias for template bindings.
   */
  public resolveDestinatarioClass(destinatario?: string): string {
    return this.resolveRecipientBadgeClass(destinatario);
  }

  /**
   * Closes the active dialog modal instance.
   *
   * Description:
   *   Triggers dialogReference.close().
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleCloseModal(): void {
    this.dialogReference.close();
  }
}
